import React, { FC } from "react";
import { Table, Avatar } from "antd";
import { UserOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  BeamPayment,
  PaymentStatus,
} from "./types";
import BeamIcon from "./BeamIcon";
import "./MessageTypes.scss";

/**
 * Payments message type — a transaction ledger.
 *
 * Amounts are rendered in a tabular monospace figure so the decimal points line
 * up column-wise; status is a filled pill because it is the value people scan
 * for first.
 */

interface PaymentsCardProps {
  payments: BeamPayment[];
  onOpenProfile?: (payment: BeamPayment) => void;
  onOpenInvoice?: (payment: BeamPayment) => void;
}

const STATUS_LABEL: Record<PaymentStatus, string> = {
  settled: "Settled",
  pending: "Pending",
  failed: "Failed",
};

const formatAmount = (amount: number, currency: string) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(amount);

const PaymentsCard: FC<PaymentsCardProps> = ({
  payments,
  onOpenProfile,
  onOpenInvoice,
}) => {
  const total = payments
    .filter((p) => p.status === "settled")
    .reduce((sum, p) => sum + p.amount, 0);

  const columns = [
    {
      title: "Date & Time",
      dataIndex: "timestamp",
      width: 150,
      render: (timestamp: string) => (
        <span className="mt-pay__stamp">
          {moment(timestamp).format("D MMM YYYY")}
          <em>{moment(timestamp).format("h:mm A")}</em>
        </span>
      ),
    },
    {
      title: "Payer",
      dataIndex: "payerName",
      render: (_: string, row: BeamPayment) => (
        <button
          type="button"
          className="mt-pay__payer"
          onClick={() => onOpenProfile?.(row)}
        >
          <Avatar size={26} src={row.avatar} icon={<UserOutlined />} />
          <span>
            <b>{row.payerName}</b>
            <em>{row.payerId}</em>
          </span>
        </button>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      width: 110,
      align: "right" as const,
      render: (amount: number, row: BeamPayment) => (
        <span className="mt-pay__amount">
          {formatAmount(amount, row.currency)}
        </span>
      ),
    },
    {
      title: "Method",
      dataIndex: "method",
      width: 130,
      render: (method: string) => (
        <span className="mt-pay__method">{method}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 100,
      render: (status: PaymentStatus) => (
        <span className={`mt-pill mt-pill--${status}`}>
          {STATUS_LABEL[status]}
        </span>
      ),
    },
    {
      title: "Invoice",
      dataIndex: "invoiceRef",
      width: 80,
      align: "center" as const,
      render: (_: string, row: BeamPayment) => (
        <button
          type="button"
          className="mt-pay__invoice"
          title={row.invoiceRef}
          onClick={() => onOpenInvoice?.(row)}
        >
          <BeamIcon name="doc" size={15} />
        </button>
      ),
    },
  ];

  return (
    <section className="mt-card mt-pay">
      <header className="mt-card__head">
        <h4>Payments</h4>
        <span className="mt-pay__total">
          Settled total <b>{formatAmount(total, payments[0]?.currency || "USD")}</b>
        </span>
      </header>

      <div className="mt-card__body mt-card__body--flush">
        <Table
          className="mt-pay__table"
          rowKey="id"
          size="small"
          pagination={false}
          dataSource={payments}
          columns={columns as any}
          // Alternating rows so a long ledger stays readable across columns.
          rowClassName={(_, index) =>
            index % 2 === 1 ? "mt-pay__row--alt" : ""
          }
          locale={{ emptyText: "No transactions yet." }}
        />
      </div>
    </section>
  );
};

export default PaymentsCard;
